import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import StarRating from '../components/StarRating';
import ShareButton from '../components/ShareButton';
import StrengthsGapsChips from '../components/company/StrengthsGapsChips';
import PillarStrip from '../components/company/PillarStrip';

import { useCompanyData } from '../hooks/useCompanyData';
import {
  useIndustryComparisons,
  getIndustryDataForCompany,
  getCompanyDetailsFromRankings,
} from '../hooks/useIndustryComparisons';

import OverviewTab from '../components/company/tabs/OverviewTab';
import MethodologyTab from '../components/company/tabs/MethodologyTab';
import SourcesTab from '../components/company/tabs/SourcesTab';

import { getGradeColor, getGradeFromScore } from '../utils/colorMapping';

import {
  Shield,
  Eye,
  Scale,
  Building,
  Users,
  Brain,
  ArrowLeft,
  Award,
  BookOpen,
} from 'lucide-react';

function CompanyPage() {
  const [activeTab, setActiveTab] = useState('overview');

  const { slug } = useParams();

  const { companyData, loading, error } = useCompanyData(slug);
  const { industryComparisons } = useIndustryComparisons();

  const industryData = getIndustryDataForCompany(companyData, industryComparisons);
  const companyDetails = getCompanyDetailsFromRankings(companyData, industryComparisons);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900">Loading assessment&hellip;</h2>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !companyData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Company data not found</h2>
          <p className="text-gray-600 mt-2">Please try again or select a different company</p>
          <Link
            to="/companies"
            className="mt-4 inline-flex items-center gap-2 text-blue-600 hover:text-blue-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Companies
          </Link>
        </div>
      </div>
    );
  }

  // --- Derived values ---
  const totalScore = companyData.total_score ?? 0;
  const maxScore = companyData.max_possible_score ?? 14;

  const PILLAR_ICONS = {
    transparency: Eye,
    fairness: Scale,
    explainability: Brain,
    oversight: Users,
    privacy: Shield,
    governance: Building,
    external_accountability: Award,
  };

  const formatMarketCap = (value) => {
    if (!value) return 'N/A';
    if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    return `$${value}`;
  };

  const formatEmployeeCount = (count) => {
    if (!count) return 'N/A';
    if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
    return count.toString();
  };

  const formatWebsiteUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://${url}`;
  };

  const overallGrade = getGradeFromScore(totalScore, maxScore);
  const gradeColor = getGradeColor(overallGrade);

  const ordinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const overallFindings = companyData.overall_findings || '';

  // Trust metadata — year only for now
  const publishedYear = companyData.published_at
    ? new Date(companyData.published_at).getFullYear()
    : null;
  const sourcesUsed = companyData.total_sources_used;
  const evidenceItems = companyData.total_evidence_items;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>
          {companyData
            ? `${companyData.name} RAI Score: ${overallGrade} Grade | RAI Scores`
            : 'RAI Scores: Company'}
        </title>
        <meta
          name="description"
          content={
            overallFindings
              ? `${companyData.name} RAI rating: ${overallGrade} (${totalScore}/${maxScore}). ${overallFindings.substring(0, 120)}...`
              : companyData?.summary
              ? `${companyData.name} RAI rating: ${overallGrade} (${totalScore}/${maxScore}). ${companyData.summary.substring(0, 120)}...`
              : `${companyData.name}: independent, evidence-based assessment of responsible AI governance.`
          }
        />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="RAI Scores" />
        <meta
          property="og:title"
          content={`${companyData?.name || 'Company'} RAI Score: ${overallGrade} Grade`}
        />
        <meta
          property="og:description"
          content={`${companyData.name} scored ${overallGrade} (${totalScore}/${maxScore}) in RAI Scores' independent, evidence-based assessment of responsible AI governance.`}
        />
        <meta
          property="og:url"
          content={`https://raiscores.com/company/${companyData?.slug || ''}`}
        />
        <meta property="og:image" content="https://raiscores.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content={`${companyData?.name || 'Company'} RAI Score: ${overallGrade} Grade`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@raiscores" />
        <meta name="twitter:creator" content="@raiscores" />
        <meta
          name="twitter:title"
          content={`${companyData?.name || 'Company'} RAI Score: ${overallGrade}`}
        />
        <meta
          name="twitter:description"
          content={
            companyData?.name
              ? `${companyData.name} scored ${overallGrade} (${totalScore}/${maxScore}) across 7 responsible-AI pillars in an independent, public-evidence assessment.`
              : 'Independent, evidence-based ratings of responsible AI governance'
          }
        />
        <meta name="twitter:image" content="https://raiscores.com/og-image.png" />
        <meta property="og:locale" content="en_US" />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href={`https://raiscores.com/company/${companyData?.slug || ''}`}
        />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': ['Organization', 'Article'],
            name: companyData?.name || '',
            url: companyData?.website || '',
            sameAs: companyData?.website || '',
            description: companyData?.summary || '',
            foundingDate: companyData?.founded_year || '',
            numberOfEmployees: companyData?.employee_count || '',
            industry: companyData?.industry || '',
            marketCapitalization: companyData?.market_cap_usd || '',
            headline: `${companyData?.name || ''} RAI Score: ${overallGrade} Grade`,
            about: {
              '@type': 'Thing',
              name: 'Responsible AI Assessment',
              description:
                'Independent evaluation of AI ethics and governance practices',
            },
            publisher: {
              '@type': 'Organization',
              name: 'RAI Scores',
              url: 'https://raiscores.com',
              logo: {
                '@type': 'ImageObject',
                url: 'https://raiscores.com/logo.png',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://raiscores.com/company/${companyData?.slug || ''}`,
            },
            image: 'https://raiscores.com/og-image.png',
            datePublished: companyData?.published_at || '',
            dateModified: companyData?.published_at || '',
            // aggregateRating intentionally omitted: that schema type is for
            // collected user reviews; using it for editorial scores risks a
            // structured-data penalty
          })}
        </script>
      </Helmet>

      {/* Sticky nav breadcrumb */}
      <nav className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/companies"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Companies</span>
            </Link>
            <div className="text-gray-400 hidden sm:block">|</div>
            <div className="text-sm text-gray-600 hidden sm:block">
              {companyData.industry} &gt; {companyData.name}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ShareButton
              url={`https://www.raiscores.com/company/${companyData?.slug || ''}`}
              title={`RAI Scores: ${companyData?.name || ''}`}
              description={
                overallFindings ||
                companyData?.summary ||
                `${companyData?.name}: Responsible AI assessment`
              }
              companyName={companyData?.name}
              companyGrade={overallGrade}
            />
          </div>
        </div>
      </nav>

      {/* ========== HERO SECTION ========== */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left: Company identity + narrative */}
            <div className="flex-1 min-w-0 order-2 lg:order-1">
              {/* Company name & meta line */}
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                {companyData.name}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mb-6">
                <span className="flex items-center gap-1">
                  <Building className="w-4 h-4" />
                  {companyData.industry}
                </span>
                {companyData.ticker && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className="font-medium text-gray-600">{companyData.ticker}</span>
                  </>
                )}
                {companyData.fortune_rank && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span>Fortune #{companyData.fortune_rank}</span>
                  </>
                )}
                {companyData.website && (
                  <>
                    <span className="text-gray-300">|</span>
                    <a
                      href={formatWebsiteUrl(companyData.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {companyData.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </a>
                  </>
                )}
              </div>

              {/* Strengths & Gaps chips */}
              <StrengthsGapsChips
                strengths={companyData.key_strengths}
                gaps={companyData.key_gaps}
              />

              {/* Overall findings narrative */}
              {overallFindings && (
                <div className="mt-5">
                  <p className="text-gray-700 leading-relaxed text-sm">
                    {overallFindings}
                  </p>
                </div>
              )}

            </div>

            {/* Right: Rating seal — shows first on mobile */}
            <div className="lg:w-64 flex-shrink-0 order-1 lg:order-2">
              <div className="bg-white rounded-xl border border-gray-200 shadow-md p-5">
                {/* Eyebrow */}
                <div className="flex items-center justify-between mb-5">
                  <span className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-gray-400">
                    RAI Rating
                  </span>
                  {publishedYear && (
                    <span className="font-mono text-[10px] text-gray-400">{publishedYear}</span>
                  )}
                </div>

                {/* Grade */}
                <div className={`text-7xl font-bold leading-none text-center mb-3 ${gradeColor}`}>
                  {overallGrade}
                </div>

                {/* Stars */}
                <div className="flex justify-center mb-2">
                  <StarRating rating={companyData.star_rating ?? 0} size={20} />
                </div>

                {/* Score */}
                <div className="font-mono text-sm text-center text-gray-700 mb-5">
                  {totalScore}/{maxScore}
                  <span className="text-gray-400"> total score</span>
                </div>

                {/* Pillar strip */}
                {companyData.pillar_scores && (
                  <div className="text-center mb-5">
                    <PillarStrip pillarScores={companyData.pillar_scores} size="md" />
                    <div className="font-mono text-[10px] uppercase tracking-wider text-gray-400 mt-1.5">
                      7 pillars · 2 pts each
                    </div>
                  </div>
                )}

                {/* Industry context — `> 0` comparisons avoid JSX rendering literal 0s */}
                {(companyDetails?.industry_rank > 0 || companyDetails?.industry_percentile > 0) && (
                  <div className="text-[13px] text-center text-gray-600">
                    {companyDetails?.industry_rank > 0 && (
                      <span>#{companyDetails.industry_rank} in industry</span>
                    )}
                    {companyDetails?.industry_rank > 0 && companyDetails?.industry_percentile > 0 && (
                      <span className="text-gray-300"> · </span>
                    )}
                    {companyDetails?.industry_percentile > 0 && (
                      <span>{ordinal(companyDetails.industry_percentile)} percentile</span>
                    )}
                  </div>
                )}

                {/* Trust stamp */}
                {(sourcesUsed > 0 || evidenceItems > 0) && (
                  <div className="border-t border-gray-100 mt-4 pt-3 font-mono text-[10px] text-center text-gray-400">
                    {sourcesUsed ? `${sourcesUsed} sources` : ''}
                    {sourcesUsed && evidenceItems ? ' · ' : ''}
                    {evidenceItems ? `${evidenceItems} evidence items` : ''}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== MAIN CONTENT ========== */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4">
          {/* Tab navigation bar */}
          <div className="mb-8">
            <div className="flex overflow-x-auto border-b border-gray-200 scrollbar-hide">
              {['overview', 'sources', 'methodology', 'detailed-analysis', 'compare'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 font-medium capitalize transition-all duration-200 border-b-2 whitespace-nowrap ${
                    activeTab === tab
                      ? 'text-blue-600 border-blue-600'
                      : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  {tab.replace('-', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Content grid: main + sidebar */}
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Main content (3/4) */}
            <div className="lg:col-span-3">
              {activeTab === 'overview' && (
                <OverviewTab
                  companyData={companyData}
                  PILLAR_ICONS={PILLAR_ICONS}
                  onViewSources={() => setActiveTab('sources')}
                />
              )}

              {activeTab === 'sources' && <SourcesTab companyData={companyData} />}

              {activeTab === 'methodology' && <MethodologyTab />}

              {/* DETAILED ANALYSIS TAB — in development */}
              {activeTab === 'detailed-analysis' && (
                <div className="bg-white rounded-xl p-6 md:p-8 shadow-md border border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Detailed Analysis</h2>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4 max-w-2xl">
                    In development. This tab will add per-pillar improvement paths, risk
                    context, and score tracking across assessment cycles &mdash; built on
                    the same public-evidence standard as the rest of the rating.
                  </p>
                  <span className="font-mono text-[11px] uppercase tracking-wider text-gray-400">
                    Planned &middot; In development
                  </span>
                </div>
              )}

              {/* COMPARE TAB — Industry comparison table */}
              {activeTab === 'compare' && (
                <div className="space-y-8">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Compare Companies</h2>
                    <p className="text-gray-700 mb-6">
                      See how {companyData.name} stacks up against industry leaders and the broader market.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead>
                          <tr className="bg-gray-100 text-gray-700">
                            <th className="px-4 py-2 text-left font-semibold">Rank</th>
                            <th className="px-4 py-2 text-left font-semibold">Company</th>
                            <th className="px-4 py-2 text-left font-semibold">Grade</th>
                          </tr>
                        </thead>
                        <tbody>
                          {industryData?.industry_leaders?.map((leader, idx) => (
                            <tr key={leader.name} className={leader.name === companyData.name ? 'bg-blue-50' : ''}>
                              <td className="px-4 py-2">{idx + 1}</td>
                              <td className="px-4 py-2 font-semibold">{leader.name}</td>
                              <td className="px-4 py-2">
                                <span className={
                                  leader.rating?.startsWith('A')
                                    ? 'text-green-600 font-medium'
                                    : 'text-blue-600'
                                }>
                                  {leader.rating}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar (1/4) */}
            <div className="space-y-6">
              {/* Assessment Details */}
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Assessment Details</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Published</span>
                    <span className="text-sm font-medium">{publishedYear || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Sources</span>
                    <span className="text-sm font-medium">{sourcesUsed ?? 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Evidence Items</span>
                    <span className="text-sm font-medium text-green-600">
                      {evidenceItems ?? 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Pillars w/ Evidence</span>
                    <span className="text-sm font-medium">
                      {companyData.pillars_with_evidence ?? 'N/A'}/7
                    </span>
                  </div>
                </div>
              </div>

              {/* Industry Rank */}
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Industry Rank</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Rank</span>
                      <span className="font-medium">
                        #{companyDetails?.industry_rank || 'N/A'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{
                          width: `${companyDetails?.industry_percentile || 0}%`,
                        }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {companyDetails?.industry_percentile
                        ? `Top ${companyDetails.industry_percentile}% of ${industryData?.total_companies || 'N/A'} ${companyData.industry} companies`
                        : `${industryData?.total_companies || 'N/A'} ${companyData.industry} companies evaluated`}
                    </div>
                  </div>

                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-sm text-gray-600 mb-2">Industry Leaders</div>
                    <div className="space-y-1 text-sm">
                      {industryData?.industry_leaders?.map((leader, index) => (
                        <div key={leader.name} className="flex justify-between">
                          <span
                            className={
                              leader.name === companyData.name ? 'font-bold' : ''
                            }
                          >
                            {index + 1}. {leader.name}
                          </span>
                          <span
                            className={
                              leader.rating?.startsWith('A')
                                ? 'text-green-600'
                                : 'text-blue-600'
                            }
                          >
                            {leader.rating}
                          </span>
                        </div>
                      ))}

                      {/* Show current company if not in top 3 */}
                      {!industryData?.industry_leaders?.some(
                        (l) => l.name === companyData.name
                      ) &&
                        companyDetails && (
                          <div className="flex justify-between font-bold mt-2 border-t pt-2">
                            <span>
                              {companyDetails.industry_rank}. {companyDetails.name}
                            </span>
                            <span
                              className={
                                companyDetails.rating?.startsWith('A')
                                  ? 'text-green-600'
                                  : 'text-blue-600'
                              }
                            >
                              {companyDetails.rating}
                            </span>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>

              {/* How We Score */}
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-3">How We Score</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Companies are assessed across 7 pillars of responsible AI using publicly
                  available evidence. Scoring is deterministic — LLMs extract evidence, but all
                  scoring is rule-based.
                </p>
                <Link
                  to="/methodology"
                  className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  <BookOpen className="w-4 h-4" />
                  Full methodology
                </Link>
              </div>

              {/* About Company — collapsible sidebar card */}
              <details className="group bg-white rounded-xl border border-gray-100 overflow-hidden">
                <summary className="font-semibold text-gray-900 p-5 cursor-pointer hover:bg-gray-50 transition-colors list-none flex items-center justify-between">
                  <span>About {companyData.name}</span>
                  <span className="text-gray-400 text-xs transition-transform group-open:rotate-90">▸</span>
                </summary>
                <div className="px-5 pb-5 space-y-4">
                  {companyData.summary && (
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {companyData.summary}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {companyData.founded_year && (
                      <div>
                        <div className="text-gray-500 text-xs">Founded</div>
                        <div className="font-medium text-gray-900">{companyData.founded_year}</div>
                      </div>
                    )}
                    {companyData.employee_count && (
                      <div>
                        <div className="text-gray-500 text-xs">Employees</div>
                        <div className="font-medium text-gray-900">{formatEmployeeCount(companyData.employee_count)}</div>
                      </div>
                    )}
                    {companyData.market_cap_usd && (
                      <div>
                        <div className="text-gray-500 text-xs">Market Cap</div>
                        <div className="font-medium text-gray-900">{formatMarketCap(companyData.market_cap_usd)}</div>
                      </div>
                    )}
                    {companyData.headquarters && (
                      <div>
                        <div className="text-gray-500 text-xs">Headquarters</div>
                        <div className="font-medium text-gray-900">{companyData.headquarters.replace(/, United States$/, '')}</div>
                      </div>
                    )}
                  </div>
                  {companyData.website && (
                    <a
                      href={formatWebsiteUrl(companyData.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      {companyData.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </a>
                  )}
                </div>
              </details>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

export default CompanyPage;
